// Blocker data layer (arc 101 slice 11 Part B, plan doc 07 step 5) — the UI half
// of the one capability that had an MCP verb, a durable table and no human door
// at all. Until this screen, a blocker was answerable only by an agent driving
// `blocker answer`, which is precisely the caller the verb REFUSES: it needs a
// user principal and tells the rest to "answer from the dashboard".
//
// A blocker is a hold the platform is allowed to stop for — a prerequisite only a
// person can supply. Answering does NOT restart the stopped run. What it does is one
// of three arms (`apps/api/src/routes/blockers.ts` `answerChanged`): it files a raw
// task carrying the ask and the answer, which triage decides; or it releases the
// held task the blocker was holding, with fresh attempts; or the blocker was tied to
// a live task, which carries on by itself. {@link readBlockerAnswer} names the arm
// from the 200 body, and the copy on the screen follows it, because a user told
// "nothing resumed" when something did, or "back in line" when nothing was, has been
// told the wrong thing.
//
// THE ANSWER TEXT IS NOT READABLE BACK. `maskBlocker` deliberately omits it and
// serves `answered: boolean` instead — the field a human types into is the one
// most likely to receive a pasted credential, so it is not sprayed across every
// broad read. Nothing here may assume otherwise.

import type {
  BlockerGuidance,
  BlockerKind,
  BlockerNeeds,
  BlockerOptions,
  BlockerResolution,
  TaskLlmPreference,
} from '@developerz/domain';
import { BLOCKER_LIST_MAX_PER_PAGE } from '@developerz/domain';
import { useQueryClient } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { createLiveQuery } from './live/live-query';
import { SESSION_TOKEN } from './session';

export type { BlockerGuidance, BlockerKind, BlockerNeeds, BlockerOptions, BlockerResolution };

/**
 * One blocker as `/v1/blockers` serves it — the API's `BlockerView`, which is
 * `maskBlocker` (`packages/mcp/src/army/task.ts`). Restated rather than imported
 * because `@developerz/mcp` is not a dashboard dependency (the `lib/artifacts.ts`
 * precedent); the field names are the wire contract both ends publish.
 *
 * `answer` is ABSENT on purpose — see the header. `needs` (the v2 form,
 * `{ version: 2, fields[], landed[] }`) and `fix_url` are null for every row
 * that carries no form or has no signable link, and NEITHER null is an error: a
 * blocker with no prefilled form is still answerable in prose.
 */
export type ApiBlocker = {
  readonly id: string;
  readonly kind: BlockerKind;
  readonly summary: string;
  readonly detail: string;
  readonly repo_full_name: string | null;
  readonly run_id: string | null;
  readonly task_id: string | null;
  /** The warm-tree hint — which box holds the checkout the answer would unblock. */
  readonly runner_id: string | null;
  readonly asked_at_iso: string;
  readonly resolution: BlockerResolution | null;
  readonly answered_at_iso: string | null;
  readonly answered_by_user_id: string | null;
  /** The agent that typed the answer on that person's token (`null`: the person). */
  readonly answered_by_agent?: string | null;
  /** Whether words were supplied — never the words. */
  readonly answered: boolean;
  readonly raw_task_id: string | null;
  readonly needs: BlockerNeeds | null;
  /** The choices a decision offers (#6387); null for every ask that is not a pick. */
  readonly options: BlockerOptions | null;
  /**
   * WHAT TO DO about it, in plain language (#6679) — the steps the guidance sweep wrote
   * for an ask whose answer is work rather than a choice. `null` means the sweep has not
   * reached the row; `{kind:'none'}` that it tried and had nothing. Restated here because
   * the field is on the wire NOW (the sweep gets one attempt per blocker, so the
   * projection could not trail it); the panel that renders the steps is its own change.
   */
  readonly guidance: BlockerGuidance | null;
  /**
   * Does answering this leave an ask a triage judge reads (#6535)? FALSE on the two arms
   * that resume existing work instead: a live linked task, and a held task's.
   *
   * The server's answer, never derived here: `task_id` is NOT the question (a terminal
   * one accepts a model pick and is the majority of the rows that carry one), and the
   * liveness it turns on is the database's to read.
   */
  readonly answer_files_ask: boolean;
  readonly fix_url: string | null;
};

/**
 * An answer as the route takes it: words, or the KEY of one of the row's
 * `options` (+ the words a `note_required` choice needs). The server writes the
 * row's own label for a key, so the client never sends a label to be trusted.
 */
export type BlockerReply = (
  | { readonly answer: string }
  | { readonly choice: string; readonly note?: string }
  /**
   * "I have done the steps" (#6679 slice B): the server writes its own sentence and
   * attaches the steps to the ask, and the person's words ride along as `note`.
   */
  | { readonly done: true; readonly note?: string }
) & {
  /**
   * The model the TRIAGE JUDGE decides this answer's ask on (#6521) — one of the
   * account's own keys. Absent leaves it to the account's Triage (or Coder) route. It
   * never changes the model the resulting WORK runs on.
   */
  readonly llm?: TaskLlmPreference;
};

/**
 * What an answer that landed DID, by the route's three success arms (#6726):
 * - `triage`: the answer filed a raw task (`raw_task_id`) for triage to decide; nothing
 *   resumed;
 * - `released`: the blocker held a task, which is released with fresh attempts
 *   (`released_task.task_id` on the wire); nothing was filed;
 * - `linked`: the blocker was tied to a live task (`task_id`), which carries on by itself;
 *   nothing was filed.
 *
 * Read off the 200 body by {@link readBlockerAnswer}, never assumed.
 */
export type BlockerAnswered =
  | { readonly arm: 'triage'; readonly blocker: ApiBlocker; readonly raw_task_id: string }
  | { readonly arm: 'released'; readonly blocker: ApiBlocker; readonly task_id: string }
  | { readonly arm: 'linked'; readonly blocker: ApiBlocker; readonly task_id: string };

/**
 * The three 200 bodies `blocker.answer` serializes, restated (the {@link ApiBlocker}
 * precedent): the triage arm's `{ blocker, raw_task_id }`, `heldTaskReleasedJson` and
 * `linkedAnswerJson`. {@link readBlockerAnswer} reads an `unknown` against these; the mock
 * answer `satisfies` it.
 */
export type BlockerAnswerWire =
  | { readonly blocker: ApiBlocker; readonly raw_task_id: string }
  | {
      readonly blocker: ApiBlocker;
      readonly released_task: {
        readonly task_id: string;
        readonly status: string;
        readonly prior_attempt: number;
        readonly released_dependents: readonly string[];
        readonly saved_wip: Readonly<Record<string, unknown>>;
      };
    }
  | { readonly kind: 'linked'; readonly blocker: ApiBlocker; readonly task_id: string };

const isRecord = (v: unknown): v is Readonly<Record<string, unknown>> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A 200 from the answer route whose body {@link readBlockerAnswer} cannot read. The server
 * TOOK the answer, so a caller must not report it as a failure: it says the answer landed,
 * logs this message, and re-reads (`useBlockerAnswer` does the re-read in a `finally`).
 */
export class BlockerAnswerUnreadable extends Error {
  constructor(readonly keys: readonly string[]) {
    super(
      `POST /v1/blockers/:id/answer answered 200 with a body that matches no answer arm (keys: ${keys.join(', ')}); expected raw_task_id, released_task.task_id, or kind 'linked' with task_id`,
    );
    this.name = 'BlockerAnswerUnreadable';
  }
}

/**
 * Name the arm of an answer's 200 body (`blocker.answer`: `linkedAnswerJson`,
 * `heldTaskReleasedJson`, and the triage arm's `{ blocker, raw_task_id }`). A body that
 * matches none of them THROWS, naming the keys it carried: a new or renamed arm on the
 * route must fail here, loudly, rather than be read as whichever arm it looks most like.
 */
export function readBlockerAnswer(body: unknown): BlockerAnswered {
  if (isRecord(body) && isRecord(body['blocker'])) {
    // The row is `maskBlocker`'s, the shape every blocker read already trusts as
    // `ApiBlocker`; only the arm is judged here, which is the one thing that varies.
    const blocker = body['blocker'] as unknown as ApiBlocker;
    const released = body['released_task'];
    if (body['kind'] === 'linked' && typeof body['task_id'] === 'string') {
      return { arm: 'linked', blocker, task_id: body['task_id'] };
    }
    if (isRecord(released) && typeof released['task_id'] === 'string') {
      return { arm: 'released', blocker, task_id: released['task_id'] };
    }
    if (typeof body['raw_task_id'] === 'string') {
      return { arm: 'triage', blocker, raw_task_id: body['raw_task_id'] };
    }
  }
  throw new BlockerAnswerUnreadable(isRecord(body) ? Object.keys(body) : [String(body)]);
}

export const BLOCKERS_QUERY_KEY = 'blockers';

/**
 * The route's own `per_page` ceiling ({@link BLOCKER_LIST_MAX_PER_PAGE}, the domain
 * constant `GET /v1/blockers` refuses past). Asked for on every read so one
 * round trip shows as much as the server will serve, and compared against the row
 * count so a FULL page can be labelled truncatable rather than passed off as the
 * whole record. There is no cursor to page with, so there is no Load-more.
 */
export const BLOCKER_PAGE_MAX = BLOCKER_LIST_MAX_PER_PAGE;

/**
 * The account's blockers, OLDEST FIRST — that is the port's order and it is the
 * right one here: the longest-owed answer is the most overdue. `open` is part of
 * the query key, so widening to the closed record re-reads server-side instead of
 * filtering a head the client happens to hold.
 *
 * LIVE (`createLiveQuery`, #7612): any change on the `blockers` topic re-reads it.
 * The topic carries OPEN blockers only, and a close arrives there as a `deleted`
 * patch — so the closed record (`open() === false`) hears its own growth too. The
 * REST read stays the door because `fix_url` is never on the topic's row.
 */
export function useBlockers(open: () => boolean, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<readonly ApiBlocker[]>(
    () => ({
      queryKey: [BLOCKERS_QUERY_KEY, open(), token, activeAccountId()],
      enabled: token !== null,
      live: [{ entity: 'blocker' }],
      queryFn: async () =>
        (
          await apiFetch<{ blockers: ApiBlocker[] }>(
            `/v1/blockers?open=${open()}&per_page=${BLOCKER_PAGE_MAX}`,
            token,
          )
        ).blockers,
    }),
    token,
  ).query;
}

/**
 * Supply what was missing. Closes the row and starts work by one of three arms
 * ({@link BlockerAnswered}): a raw task filed for triage, a held task released, or a
 * linked live task carrying on. That is why it spends `write:tasks` rather than a read
 * scope. A second answer is refused (409), never doubled.
 */
export async function answerBlocker(
  id: string,
  reply: BlockerReply,
  token: string | null = SESSION_TOKEN,
): Promise<BlockerAnswered> {
  return readBlockerAnswer(
    await apiFetch<unknown>(`/v1/blockers/${encodeURIComponent(id)}/answer`, token, {
      method: 'POST',
      body: JSON.stringify(reply),
    }),
  );
}

/**
 * The Fleet page's answer call (#6726): {@link answerBlocker} with the page's token, then the
 * blockers re-read in a `finally` (every `BLOCKERS_QUERY_KEY` query, so the rail's count
 * follows too). NOT awaited: a failed re-read is that query's own error, never a refusal of an answer the
 * server took. Call it in a component under the app's `QueryClientProvider`; the card it
 * feeds takes it as a prop, so the card renders without one.
 */
export function useBlockerAnswer(
  token: string | null = SESSION_TOKEN,
): (id: string, reply: BlockerReply) => Promise<BlockerAnswered> {
  const client = useQueryClient();
  return async (id, reply) => {
    try {
      return await answerBlocker(id, reply, token);
    } finally {
      // Either way: a refusal re-reads what is true now, and a 200 the reader rejects
      // ({@link BlockerAnswerUnreadable}) still changed the row on the server.
      void client.invalidateQueries({ queryKey: [BLOCKERS_QUERY_KEY] });
    }
  };
}

/** Retire an ask that went away. Closes the row `withdrawn`; nothing is dispatched. */
export async function withdrawBlocker(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<ApiBlocker> {
  return (
    await apiFetch<{ blocker: ApiBlocker }>(
      `/v1/blockers/${encodeURIComponent(id)}/withdraw`,
      token,
      { method: 'POST' },
    )
  ).blocker;
}

/**
 * What the `?blocker=` deep link off the fleet notification resolves to.
 *
 * A DISCRIMINATED VERDICT rather than a filtered array, because "the id is not in
 * this list" and "this list is empty" are different facts with different remedies
 * and an empty array collapses them. The notification points at ONE ask; landing
 * on 80 rows is not answering it, and landing on a blank page when the row has
 * since been answered reads as a broken link.
 */
export type BlockerFocus =
  | { readonly kind: 'all'; readonly rows: readonly ApiBlocker[] }
  | { readonly kind: 'focused'; readonly rows: readonly ApiBlocker[] }
  | { readonly kind: 'not_here' };

/** Resolve `rows` against the `?blocker=` param. `null`/`''` means "no deep link". */
export function focusBlockers(rows: readonly ApiBlocker[], blockerId: string | null): BlockerFocus {
  if (blockerId === null || blockerId === '') return { kind: 'all', rows };
  const row = rows.find((b) => b.id === blockerId);
  return row === undefined ? { kind: 'not_here' } : { kind: 'focused', rows: [row] };
}

/**
 * The OPEN access asks on one repo, in the list's own order — what the repo
 * view's access panel renders. An access ask is a row carrying a needs FORM
 * (only a `missing_credential` may, `BlockerDraftSchema`); prose-only asks stay
 * on the Blockers page, where they can be answered.
 *
 * A filter over the account's bounded open head ({@link BLOCKER_PAGE_MAX}),
 * because `GET /v1/blockers` takes no repo argument. The narrowing is display
 * only: the Blockers page stays the one place a row is answered or withdrawn.
 */
export function openAccessBlockers(
  rows: readonly ApiBlocker[],
  fullName: string,
): readonly ApiBlocker[] {
  return rows.filter(
    (b) => b.resolution === null && b.needs !== null && b.repo_full_name === fullName,
  );
}
